import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { EMPTY_OBJECT } from 'Utils/stableDefaults'

import useSubscriptionSettingsTracking from '../../hooks/useSubscriptionSettingsTracking'

const OTHER_VALUE = 'other'
const OTHER_PREFIX = 'other: '
const FEE_PREFIX = 'early_cancellation_fee: '
// subscriptions.cancel_reason is varchar(255); both prefixes are reserved unconditionally so the
// textarea cap does not shrink once fee data lands and truncate text the user already typed.
const MAX_REASON_LENGTH = 255
const MAX_CUSTOM_REASON_LENGTH = MAX_REASON_LENGTH - FEE_PREFIX.length - OTHER_PREFIX.length
const PLAN_MONTHLY = 'monthly'

export const CANCEL_MODAL_STEP = {
  PAUSE_OFFER: 'pause_offer',
  REASON: 'reason',
  CANCEL: 'cancel',
}

// Shared with the settings hook, which reports the entry step on the cancel-click event: two
// copies of this rule would drift into reporting a step the user never lands on.
export const resolveEntryStep = ({ showPauseOffer, hasReasonStep }) => {
  if (showPauseOffer) return CANCEL_MODAL_STEP.PAUSE_OFFER

  return hasReasonStep ? CANCEL_MODAL_STEP.REASON : CANCEL_MODAL_STEP.CANCEL
}

function parseAmount(value) {
  if (typeof value === 'number') return value

  return parseFloat(String(value).replace(/[^0-9.]/g, '')) || 0
}

// UTC, not the browser zone: the same instant is formatted server-side by
// Profile::SubscriptionSerializer#period_end_date with strftime in the Rails zone (UTC), so a
// local-zone format here makes the PlanCard and this modal disagree by a day west of UTC.
function formatAccessDate(isoDate, locale = 'en') {
  return new Date(isoDate).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

function useCancelModal({
  isOpen = false,
  onClose,
  onSubmit,
  onReasonCapture = null,
  subscriptionId,
  trackingData = EMPTY_OBJECT,
  feeData,
  feeError,
  isFeeLoading = false,
  locale,
  subscriptionV16Enabled = false,
  planType = 'yearly',
  showPauseOffer = false,
  onPause = null,
  isPauseLoading = false,
}) {
  // v1.6 flow inserts a dedicated survey step (path-independent, required) between the
  // pause offer and the consequences screen; flag-off keeps the legacy single screen.
  const hasReasonStep = subscriptionV16Enabled
  const initialStep = resolveEntryStep({ showPauseOffer, hasReasonStep })
  const [step, setStep] = useState(initialStep)
  const [selectedReason, setSelectedReason] = useState(null)
  const [otherText, setOtherText] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isAcknowledged, setIsAcknowledged] = useState(false)
  const {
    trackCancellationConfirmed,
    trackPauseOfferViewed,
    trackPauseClick,
    trackPauseOfferDeclined,
    trackPauseOfferDismissed,
  } = useSubscriptionSettingsTracking(trackingData)
  const trackedRef = useRef({})

  // Each funnel event fires at most once per entry: back-navigation returns to the offer step,
  // so an unguarded handler would count one entry twice. Reopening is a new entry.
  const trackOnce = useCallback((key, track) => {
    if (trackedRef.current[key]) return

    trackedRef.current[key] = true
    track(subscriptionId)
  }, [subscriptionId])

  // The modal stays mounted across opens while pause eligibility can change in place
  // (committing or canceling a plan switch), so re-derive the entry step on every open
  // instead of trusting the state captured on mount or at the previous close.
  useEffect(() => {
    if (isOpen) setStep(initialStep)
  }, [isOpen, initialStep])

  useEffect(() => {
    if (!isOpen) {
      trackedRef.current = {}
      return
    }

    if (step !== CANCEL_MODAL_STEP.PAUSE_OFFER) return

    trackOnce('viewed', trackPauseOfferViewed)
  }, [isOpen, step, trackOnce, trackPauseOfferViewed])

  const isOtherSelected = selectedReason?.value === OTHER_VALUE

  // Plan-type fact, independent of the flag: a monthly plan never has an early-cancellation fee,
  // so flag-off monthly subscribers must not hit the fee path (no fee modal, no fee charge).
  const isMonthly = planType === PLAN_MONTHLY

  const feeBreakdown = useMemo(() => {
    if (!feeData) return null

    const cancelFee = parseAmount(feeData.cancel_fee)
    if (!cancelFee) return null

    return {
      remainingMonths: feeData.remaining_months,
      monthlyRate: parseAmount(feeData.monthly_rate),
      totalRemaining: parseAmount(feeData.total_remaining),
      cancelFee,
    }
  }, [feeData])

  const hasFee = Boolean(feeBreakdown) && !isMonthly

  // Flag-off keeps the legacy inline select on the consequences screen (no-fee path only,
  // the flag-off fee path captures no reason — pre-v1.6 behavior).
  const showInlineReasonSelect = !hasReasonStep && !hasFee
  const isReasonRequired = hasReasonStep || showInlineReasonSelect

  const accessUntilFormatted = useMemo(() => {
    if (!feeData) return null

    return formatAccessDate(feeData.access_until, locale)
  }, [feeData, locale])

  // Closing during an in-flight pause is blocked at the Modal level (disableClose), never
  // here: Triangle Modal flips its internal state before calling onClose, so a no-op close
  // callback would desync it from the parent and leave the modal unable to reopen.
  const handleClose = useCallback(() => {
    // A successful pause closes the modal from the parent, and Triangle Modal routes that
    // through onClose while the step is still the offer. Accepting is not abandoning.
    if (step === CANCEL_MODAL_STEP.PAUSE_OFFER && !trackedRef.current.accepted) {
      trackOnce('dismissed', trackPauseOfferDismissed)
    }

    setStep(initialStep)
    setSelectedReason(null)
    setOtherText('')
    setIsSubmitting(false)
    setIsAcknowledged(false)
    onClose()
  }, [step, trackOnce, trackPauseOfferDismissed, initialStep, onClose])

  // The pause-offer -> cancel transition stays locked while a pause request is in flight:
  // the backend reserves user_pause_scheduled_at before responding, and a cancellation
  // submitted in that window would double-schedule pause + cancel at Solidgate.
  const handleContinueToCancel = useCallback(() => {
    if (isPauseLoading) return

    trackOnce('declined', trackPauseOfferDeclined)
    setStep(hasReasonStep ? CANCEL_MODAL_STEP.REASON : CANCEL_MODAL_STEP.CANCEL)
  }, [isPauseLoading, trackOnce, trackPauseOfferDeclined, hasReasonStep])

  const handlePauseClick = useCallback(() => {
    if (isPauseLoading) return

    trackOnce('accepted', trackPauseClick)
    onPause?.()
  }, [isPauseLoading, trackOnce, trackPauseClick, onPause])

  const handleReasonChange = useCallback((option) => {
    setSelectedReason(option)

    if (option?.value !== OTHER_VALUE) {
      setOtherText('')
    }
  }, [])

  const handleOtherTextChange = useCallback((e) => {
    setOtherText(e.target.value)
  }, [])

  // onSubmit resolves false (or throws) when the cancellation did not go through — only a
  // confirmed cancellation may track as confirmed and close the modal, otherwise the user
  // sees the error toast on a modal that already reported success.
  const submitAndTrack = useCallback(async (reason, trackingArgs) => {
    setIsSubmitting(true)

    let succeeded
    try {
      succeeded = await onSubmit(reason)
    } catch {
      setIsSubmitting(false)
      return
    }

    if (succeeded === false) {
      setIsSubmitting(false)
      return
    }

    trackCancellationConfirmed(...trackingArgs)
    handleClose()
  }, [onSubmit, trackCancellationConfirmed, handleClose])

  const resolveReasonPayload = useCallback(() => {
    if (!selectedReason) return null

    const isOther = selectedReason.value === OTHER_VALUE
    const customReason = otherText.trim()
    if (isOther && !customReason) return null

    return {
      reason: isOther ? `${OTHER_PREFIX}${customReason}` : selectedReason.value,
      trackingArgs: [
        selectedReason.value,
        subscriptionId,
        isOther ? customReason : undefined,
      ],
    }
  }, [selectedReason, otherText, subscriptionId])

  // Capture the reason as the user advances OUT of the survey step, not only on final
  // confirm — users who then see the fee or the consequences and decide to keep the
  // subscription would otherwise never have their reason stored.
  const handleContinueFromReason = useCallback(() => {
    const payload = resolveReasonPayload()
    if (!payload) return

    onReasonCapture?.(payload.reason)
    setStep(CANCEL_MODAL_STEP.CANCEL)
  }, [resolveReasonPayload, onReasonCapture])

  const handleBackFromReason = useCallback(() => {
    if (!showPauseOffer) return

    setStep(CANCEL_MODAL_STEP.PAUSE_OFFER)
  }, [showPauseOffer])

  const handleBackToReason = useCallback(() => {
    if (!hasReasonStep) return

    setStep(CANCEL_MODAL_STEP.REASON)
  }, [hasReasonStep])

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return

    if (hasFee && !isAcknowledged) return

    if (isReasonRequired) {
      const payload = resolveReasonPayload()
      if (!payload) return

      const reason = hasFee
        ? `${FEE_PREFIX}${payload.reason}`
        : payload.reason
      const trackingArgs = hasFee
        ? ['early_cancellation_fee', subscriptionId, payload.reason]
        : payload.trackingArgs

      await submitAndTrack(reason, trackingArgs)
      return
    }

    await submitAndTrack(
      'early_cancellation_fee',
      ['early_cancellation_fee', subscriptionId]
    )
  }, [
    hasFee,
    isAcknowledged,
    isSubmitting,
    isReasonRequired,
    resolveReasonPayload,
    submitAndTrack,
    subscriptionId,
  ])

  const isReasonMissing = isReasonRequired && (!selectedReason || (isOtherSelected && !otherText.trim()))
  const isAckMissing = hasFee && !isAcknowledged
  // isFeeLoading covers the pause-offer entry, where the fee prefetches in the background:
  // submitting before it settles would let a fee-bearing cancel through as a no-fee one.
  const isSubmitDisabled = !!feeError || isSubmitting || isFeeLoading || isReasonMissing || isAckMissing

  return {
    step,
    handleContinueToCancel,
    handlePauseClick,
    handleContinueFromReason,
    handleBackFromReason,
    handleBackToReason,
    isContinueFromReasonDisabled: isReasonMissing,
    hasReasonStep,
    feeBreakdown,
    hasFee,
    feeError,
    showInlineReasonSelect,
    accessUntilFormatted,
    isAcknowledged,
    setIsAcknowledged,
    selectedReason,
    isOtherSelected,
    otherText,
    handleReasonChange,
    handleOtherTextChange,
    handleSubmit,
    handleClose,
    isSubmitDisabled,
    isSubmitting,
    maxCustomReasonLength: MAX_CUSTOM_REASON_LENGTH,
  }
}

export default useCancelModal
